/**
 * Statement-level ratio derivations (ADR-0035). Missing legs are null, never 0
 * (R2b). Distinct from TTM `StockRatios.de` / `currentRatio`.
 */

function finitePositive(value: number | null): number | null {
  if (value === null || !Number.isFinite(value) || value <= 0) return null;
  return value;
}

function finiteNumber(value: number | null): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  return value;
}

/** Newest array element that carries a usable fact. Trailing all-null years stay in the series. */
export function latestServablePeriod<T>(
  periods: readonly T[],
  usable: (row: T) => boolean,
): T | undefined {
  return periods.reduce<T | undefined>((found, row) => (usable(row) ? row : found), undefined);
}

/** Debt / equity. Null when either leg is missing or equity is not positive. */
export function debtToEquity(debt: number | null, equity: number | null): number | null {
  const d = finiteNumber(debt);
  const e = finitePositive(equity);
  if (d === null || e === null) return null;
  const ratio = d / e;
  return Number.isFinite(ratio) ? ratio : null;
}

/** Current assets / current liabilities. Null when liabilities are not positive. */
export function currentRatioFrom(
  currentAssets: number | null,
  currentLiabilities: number | null,
): number | null {
  const assets = finiteNumber(currentAssets);
  const liabilities = finitePositive(currentLiabilities);
  if (assets === null || liabilities === null) return null;
  const ratio = assets / liabilities;
  return Number.isFinite(ratio) ? ratio : null;
}

/** Revenue × operating margin. Null when either leg is missing. */
export function operatingIncome(
  revenue: number | null,
  operatingMargin: number | null,
): number | null {
  const rev = finiteNumber(revenue);
  const margin = finiteNumber(operatingMargin);
  if (rev === null || margin === null) return null;
  const value = rev * margin;
  return Number.isFinite(value) ? value : null;
}
